import { MessageScroller } from "@shadcn/react/message-scroller";
import { createFileRoute } from "@tanstack/react-router";
import { translation } from "albertine-shared-web";
import { useState } from "react";
import { ChatThreadMessages } from "./-components/ChatThreadMessages";
import { HostComposerInset } from "./-components/HostComposerInset";
import { JumpToNewMessagesButton } from "./-components/JumpToNewMessagesButton";
import { ReturnFromJumpButton } from "./-components/ReturnFromJumpButton";
import {
    ScrollToLatestOnSend,
    ScrollWithComposerInset,
} from "./-components/ScrollToLatestOnSend";
import { ChatPageProvider } from "./-hooks/useChatPage";
import { ChatUnreadProvider } from "./-hooks/useChatUnreadMessages";
import { useLoadPreviousMessages } from "./-hooks/useLoadPreviousMessages";
import { MessageJumpProvider } from "./-hooks/useMessageJump";
import { usePinToLatestUntilUserScrolls } from "./-hooks/usePinToLatestUntilUserScrolls";
import { chatSearchSchema } from "./-utils/chatSearchSchema";
import { useConciergeItemLinkNavigation } from "./-utils/conciergeItemLinkNavigation";

export const Route = createFileRoute("/chat/")({
    validateSearch: chatSearchSchema,
    component: ChatPage,
    head: () => ({ meta: [{ title: translation("chat.page_title") }] }),
});

export function ChatPage() {
    const search = Route.useSearch();
    useConciergeItemLinkNavigation();

    return (
        <ChatPageProvider search={search}>
            <main className="flex h-svh min-h-0 flex-col overflow-x-hidden bg-background-chat text-text-primary">
                <MessageScroller.Provider autoScroll defaultScrollPosition="end">
                    <ScrollToLatestOnSend />
                    <ScrollWithComposerInset />

                    <ChatUnreadProvider>
                        <MessageJumpProvider>
                            <MessageScroller.Root className="relative min-h-0 flex-1 overflow-hidden">
                                <ChatThreadViewport />

                                <JumpToNewMessagesButton />
                                <ReturnFromJumpButton />
                                <HostComposerInset />
                            </MessageScroller.Root>
                        </MessageJumpProvider>
                    </ChatUnreadProvider>
                </MessageScroller.Provider>
            </main>
        </ChatPageProvider>
    );
}

function ChatThreadViewport() {
    const [viewport, setViewport] = useState<HTMLElement | null>(null);
    useLoadPreviousMessages(viewport);
    usePinToLatestUntilUserScrolls(viewport);

    return (
        <MessageScroller.Viewport
            ref={setViewport}
            className="absolute inset-0 overflow-y-auto overflow-x-hidden"
            preserveScrollOnPrepend
        >
            <ChatThreadMessages />
        </MessageScroller.Viewport>
    );
}
